import { type PaidProjectStatus } from '#app/__generated__/api.js';

type getAllowedPaidProjectRoutesArgs = {
  activePaidProjectProposalChangeRequest?: boolean;
  isClient: boolean;
  paidProject: {
    __typename?: string;
    status: PaidProjectStatus;
  };
  visitorIsProposalSender: boolean;
};

type getAllowedPaidProjectRoutesReturnValue = {
  canConfirm: boolean;
  canEdit: boolean;
  canReview: boolean;
  canView: boolean;
};

/**
 * Util that governs which routes are allowed for a given paid project for the viewer.
 * Eg: Edit proposal page, Review Proposal/Project Page, View Project Page
 */
export const getAllowedPaidProjectRoutes = ({
  activePaidProjectProposalChangeRequest = false,
  isClient,
  paidProject,
  visitorIsProposalSender,
}: getAllowedPaidProjectRoutesArgs): getAllowedPaidProjectRoutesReturnValue => {
  switch (paidProject.status) {
    case 'CONTRACT_SIGNED':
      if (
        [
          'OngoingFixedRatePaidProject',
          'OngoingHourlyRatePaidProject',
        ].includes(paidProject?.__typename ?? '') &&
        isClient
      ) {
        return {
          canConfirm: true,
          canEdit: false,
          canReview: false,
          canView: true,
        };
      }

      if (
        [
          'OngoingFixedRatePaidProject',
          'OngoingHourlyRatePaidProject',
        ].includes(paidProject?.__typename ?? '')
      ) {
        return {
          canConfirm: false,
          canEdit: false,
          canReview: false,
          canView: true,
        };
      }

      return {
        canConfirm: false,
        canEdit: false,
        canReview: isClient,
        canView: true,
      };
    case 'INQUIRY_ACCEPTED':

    case 'PROPOSAL_DECLINED': {
      return {
        canConfirm: false,
        canEdit: visitorIsProposalSender,
        canReview: false,
        canView: true,
      };
    }

    case 'INQUIRY_RECEIVED':

    case 'INQUIRY_SUBMITTED': {
      return {
        canConfirm: false,
        canEdit: false,
        canReview: false,
        canView: false,
      };
    }

    case 'PROPOSAL_RECEIVED':

    case 'PROPOSAL_SUBMITTED': {
      return {
        canConfirm: false,
        canEdit: false,
        canReview: !visitorIsProposalSender,
        canView: true,
      };
    }

    default:
      return {
        canConfirm: false,
        canEdit: false,
        canReview: activePaidProjectProposalChangeRequest,
        canView: true,
      };
  }
};
